import { Fetch, fetchAction, SingleSocketFactory } from 'barnyard/utils';
import { normalize, schema } from 'normalizr';
import _ from 'lodash';
import { hashify_dictionary } from '~/app/common/utils';
import { getOrdersFetchingTrackers } from '~/app/common/apps/history/selectors';
import * as constants from '../constants';
import { DEFAULT_DATE_RANGE } from '~/app/corporate/dashboard/constants';


const orderItemsSchema = new schema.Entity('supplierorderitems');
const trackersSchema = new schema.Entity('trackers');
const ordersSchema = new schema.Entity('orders', {
  supplierorderitems: [orderItemsSchema],
  trackers: [trackersSchema],
});

orderItemsSchema.define({ supplier_order: ordersSchema });

const setOrderWithItems = (dispatch, orderId, json) => {
  const normalized = normalize(json, ordersSchema);

  dispatch({
    type: constants.ORDER_ITEMS_SUCCESS,
    entities: normalized.entities.supplierorderitems
  });
  dispatch({
    type: constants.ORDER_TRACKERS_FETCH.success,
    entities: normalized.entities.trackers
  });
  dispatch({
    type: constants.ORDER_FETCH.success,
    result: normalized.entities.orders[orderId],
    orderId
  });
};

const orderSearch = (query, url) => (dispatch, getState) => {
  const state = getState();
  if (state.history.order_searches[hashify_dictionary(query)]) {
    return null;
  }

  return fetchAction(
    dispatch,
    constants.ORDERS_FETCH,
    {},
    {
      url,
      body: query,
      success: (json) => {
        const normalized = normalize(json, {
          results: [ordersSchema]
        });

        dispatch({
          type: constants.ORDERS_FETCH.success,
          entities: normalized.entities.orders,
        });
        dispatch({
          type: constants.ORDERS_SEARCH_SUCCESS,
          search: hashify_dictionary(query),
          result: normalized.result
        });
      }
    }
  );
};

const pullAnalyticsFromSocket = (socketStream, { startDate, endDate }, dispatch) => {
  socketStream.sendMessage({ start_date: startDate, end_date: endDate });
  dispatch({
    type: constants.CORP_DASHBOARD_ANALYITICS.request,
  });
};

export const registerCorporateOrderAnalyticsSubscription = (dispatch, getState) => {
  const socketStream = SingleSocketFactory.getInstance(constants.CORPORATE_ANALYTICS_EVENT_TYPE);

  if (!socketStream.isRegistered) {
    socketStream.register({
      onMessage: (message) => {
        switch (message.kind) {
          case constants.CORPORATE_ANALYTICS_EVENTS.DASHBOARD: {
            dispatch({
              type: constants.CORP_DASHBOARD_ANALYITICS.success,
              result: message.result
            });
            break;
          }
          default:
            console.warn(`Received Corporate Order Analytics event type for unknown kind ${message.kind}`);
        }
      },
      onMessageError: () => {},
      onReconnect: () => {},
      onError: () => {}
    });
  }

  return socketStream;
};

export const pullAnalytics = dateRange => (dispatch, getState) => {
  const ss = registerCorporateOrderAnalyticsSubscription(dispatch, getState);
  pullAnalyticsFromSocket(ss, dateRange, dispatch);
};

export const registerOrderHistorySubscription = (dispatch, getState) => {
  const socketStream = SingleSocketFactory.getInstance(constants.ORDER_TRACKERS_FETCH_EVENT_TYPE);

  if (!socketStream.isRegistered) {
    socketStream.register({
      onMessage: (message) => {
        const normalized = normalize(message.trackers, [trackersSchema]);

        dispatch({
          type: constants.ORDER_TRACKERS_FETCH.success,
          entities: normalized.entities.trackers,
          ids: normalized.result,
          orderId: message.order_id
        });
      },
      onMessageError: () => {},
      onReconnect: () => {},
      onError: () => {
        // Get all the orders that are currently fetching
        const ordersFetchingTrackers = getOrdersFetchingTrackers(getState());
        // Mark them as failed
        _.forEach(
          ordersFetchingTrackers,
          order => dispatch({
            type: constants.ORDER_TRACKERS_FETCH.failure,
            orderId: order.id
          })
        );
      }
    });
  }

  return socketStream;
};

export const orderBuyerSearch = query => (dispatch, getState) => {
  const url = '/api/history/orders/';
  return orderSearch(query, url)(dispatch, getState);
};

export const orderCorporateSearch = query => (dispatch, getState) => {
  const url = '/api/history/corporate/orders/';
  return orderSearch(query, url)(dispatch, getState);
};

export const orderCorporateBuyerSearch = (query, buyerId) => (dispatch, getState) => {
  const url = `/api/history/corporate/buyers/${buyerId}/orders/`;
  const updatedQuery = Object.assign({ buyer: buyerId }, query);
  return orderSearch(updatedQuery, url)(dispatch, getState);
};

const orderItemSearch = (query, url) => (dispatch, getState) => {
  const state = getState();
  if (state.history.order_item_searches[hashify_dictionary(query)]) {
    return null;
  }
  return Fetch({
    url,
    body: query,
    before: () => dispatch({
      type: constants.ORDER_ITEMS_REQUEST
    }),
    success: (json) => {
      const normalized = normalize(json, {
        results: [orderItemsSchema]
      });
      dispatch({
        type: constants.ORDER_ITEMS_SUCCESS,
        entities: normalized.entities.supplierorderitems
      });
      dispatch({
        type: constants.ORDER_ITEMS_SEARCH_SUCCESS,
        search: hashify_dictionary(query),
        result: normalized.result
      });
    }
  });
};

export const orderItemBuyerSearch = query => (dispatch, getState) => {
  const url = '/api/history/orderitems/';
  return orderItemSearch(query, url)(dispatch, getState);
};

export const orderItemCorporateSearch = query => (dispatch, getState) => {
  const url = '/api/history/corporate/orderitems/';
  return orderItemSearch(query, url)(dispatch, getState);
};

export const orderItemCorporateBuyerSearch = (query, buyerId) => (dispatch, getState) => {
  const url = `/api/history/corporate/buyers/${buyerId}/orderitems/`;
  const newQuery = Object.assign({ buyer: buyerId }, query);
  return orderItemSearch(newQuery, url)(dispatch, getState);
};

export const orderTrackersFetchAction = (url, orderId) => dispatch => fetchAction(
  dispatch,
  constants.ORDER_TRACKERS_FETCH,
  { orderId },
  {
    url,
    method: 'PUT',
    success: (result) => {
      if (_.isEmpty(result)) {
        return;
      }

      const normalized = normalize(result, [trackersSchema]);

      dispatch({
        type: constants.ORDER_TRACKERS_FETCH.success,
        entities: normalized.entities.trackers,
        ids: normalized.result,
        orderId
      });
    }
  }
);

export const orderFetch = (url, orderId) => dispatch => fetchAction(
  dispatch,
  constants.ORDER_FETCH,
  { orderId },
  {
    url,
    success: (json) => {
      const normalized = normalize(json, ordersSchema);
      dispatch({
        type: constants.ORDER_ITEMS_SUCCESS,
        entities: normalized.entities.supplierorderitems
      });
      // We dispatch this here, because there isn't a separate REST call for the already-known trackers: they simply
      // hitch a ride on the order itself.
      dispatch({
        type: constants.ORDER_TRACKERS_FETCH.success,
        entities: normalized.entities.trackers,
        orderId
      });
      dispatch({
        type: constants.ORDER_FETCH.success,
        result: normalized.entities.orders[orderId],
        orderId
      });
    }
  }
);

export const orderUpdate = (url, orderId, body) => dispatch => fetchAction(
  dispatch,
  constants.ORDER_UPDATE,
  { orderId },
  {
    url,
    body,
    method: 'PATCH',
    success: json => setOrderWithItems(dispatch, orderId, json),
  }
);

export const orderItemUpdate = (url, orderId, orderItemId, body, callback = null) => dispatch => fetchAction(
  dispatch,
  constants.ORDER_ITEM_UPDATE,
  { orderId, orderItemId },
  {
    url,
    body,
    method: 'PATCH',
    success: (json) => {
      setOrderWithItems(dispatch, orderId, json);
      if (callback) {
        callback();
      }
    },
  }
);

export const buyerOrderFetch = orderId => (dispatch, getState) => {
  const url = `/api/history/orders/${orderId}/`;
  return orderFetch(url, orderId)(dispatch, getState);
};

export const buyerOrderItemUpdate = (orderId, orderItemId, body, callback) => (dispatch, getState) => {
  const url = `/api/history/orderitems/${orderItemId}/`;
  return orderItemUpdate(url, orderId, orderItemId, body, callback)(dispatch, getState);
};

export const buyerOrderTrackersFetch = orderId => (dispatch, getState) => {
  const url = `/api/history/orders/${orderId}/tracking_data/v2/`;
  return orderTrackersFetchAction(url, orderId)(dispatch, getState);
};

export const corporateOrderFetch = orderId => (dispatch, getState) => {
  const url = `/api/history/corporate/orders/${orderId}/`;
  return orderFetch(url, orderId)(dispatch, getState);
};

export const buyerOrderUpdate = (orderId, body) => (dispatch, getState) => {
  const url = `/api/history/orders/${orderId}/`;
  return orderUpdate(url, orderId, body)(dispatch, getState);
};

export const corporateOrderUpdate = (orderId, body) => (dispatch, getState) => {
  const url = `/api/history/corporate/orders/${orderId}/`;
  return orderUpdate(url, orderId, body)(dispatch, getState);
};

export const corporateOrderItemUpdate = (orderId, orderItemId, body, callback = null) => (dispatch, getState) => {
  const url = `/api/history/corporate/orderitems/${orderItemId}/`;
  return orderItemUpdate(url, orderId, orderItemId, body, callback)(dispatch, getState);
};

export const corporateOrderTrackersFetch = orderId => (dispatch, getState) => {
  const url = `/api/history/corporate/orders/${orderId}/tracking_data/v2/`;
  return orderTrackersFetchAction(url, orderId)(dispatch, getState);
};

export const orderStale = orderId => ({
  type: constants.ORDER_STALE,
  orderId,
});

export const ordersReset = () => ({
  type: constants.ORDERS_RESET
});

export const orderItemsReset = () => ({
  type: constants.ORDER_ITEMS_RESET
});

export const orderReturn = (orderId, body) => dispatch => fetchAction(
  dispatch,
  constants.ORDER_RETURN,
  { orderId },
  {
    url: `/api/orders/${orderId}/return/`,
    method: 'POST',
    body
  }
);

export const shippingLabelDelete = (orderId, trackerId) => dispatch => fetchAction(
  dispatch,
  constants.TRACKER_DELETE,
  { orderId, trackerId },
  {
    url: `/api/orders/${orderId}/tracking/${trackerId}/`,
    method: 'DELETE',
    success: (result) => {
      const normalized = normalize(result, ordersSchema);
      dispatch({
        type: constants.TRACKER_DELETE.success,
        entities: normalized.entities,
        ids: normalized.result,
        orderId
      });
    }
  }
);
